import React, { useState } from 'react';
import {
  Briefcase, Scale, Wrench, IndianRupee,
  Link2, ChevronDown, ChevronUp, Search, X
} from 'lucide-react';

// ─── Shared constants ─────────────────────────────────────────────────────────
const STATUS_LEGEND = [
  { val: '1', label: 'Lead',        bg: '#fee2e2', fg: '#991b1b', dot: '#ef4444' },
  { val: '2', label: 'In Progress', bg: '#fed7aa', fg: '#9a3412', dot: '#f97316' },
  { val: '3', label: 'Verified',    bg: '#fef9c3', fg: '#854d0e', dot: '#eab308' },
  { val: '4', label: 'Negotiation', bg: '#bbf7d0', fg: '#166534', dot: '#22c55e' },
  { val: '5', label: 'Closed',      bg: '#a7f3d0', fg: '#065f46', dot: '#10b981' },
];

const STAGE_SEQUENCE = [
  { key: 'marketing', label: 'Marketing', Icon: Briefcase,    color: '#0d9488' },
  { key: 'legal',     label: 'Legal',     Icon: Scale,        color: '#9333ea' },
  { key: 'technical', label: 'Technical', Icon: Wrench,       color: '#ea580c' },
  { key: 'finance',   label: 'Finance',   Icon: IndianRupee,  color: '#16a34a' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return null; }
};

const fmtAmt = (v) => {
  const n = Number(v);
  if (!v || isNaN(n) || n === 0) return null;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

// Pick best representative amount from a deal (stage-specific keys)
const pickAmt = (deal) =>
  fmtAmt(deal?.expected_amount) ||
  fmtAmt(deal?.barter_final_value) ||
  fmtAmt(deal?.market_value) ||
  fmtAmt(deal?.final_amount) ||
  null;

// ─── Single deal-group timeline card ─────────────────────────────────────────
export const DealGroupCard = ({ groupId, deals }) => {
  const [expanded, setExpanded] = useState(false);

  // Representative info from the first available stage
  const first = deals.marketing || deals.legal || deals.technical || deals.finance || {};
  const stagesPresent = STAGE_SEQUENCE.filter(s => deals[s.key]).length;

  // Progress line width (spans between first and last present stage)
  const firstIdx = STAGE_SEQUENCE.findIndex(s => deals[s.key]);
  const lastIdx  = [...STAGE_SEQUENCE].reverse().findIndex(s => deals[s.key]);
  const lastPresent = lastIdx >= 0 ? STAGE_SEQUENCE.length - 1 - lastIdx : 0;
  // line goes from center of firstIdx dot to center of lastPresent dot
  // each stage occupies 25% of width; dot center sits at (idx*25 + 12.5)%
  const lineLeft  = firstIdx >= 0 ? `${firstIdx * 25 + 12.5}%` : '12.5%';
  const lineWidth = (lastPresent > firstIdx && firstIdx >= 0)
    ? `${(lastPresent - firstIdx) * 25}%`
    : '0%';

  return (
    <div
      data-testid={`deal-group-card-${groupId.slice(0, 8)}`}
      className="border border-stone-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden"
    >
      {/* ── Card header ── */}
      <button
        className="w-full text-left flex items-start justify-between gap-2 px-4 py-3 bg-gradient-to-r from-stone-50 to-white border-b border-stone-100 hover:bg-stone-50 transition-colors"
        onClick={() => setExpanded(e => !e)}
        data-testid={`deal-group-expand-${groupId.slice(0, 8)}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-stone-900 text-sm">{first.customer_name || '(No Name)'}</span>
            <span className="text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5">
              <Link2 className="w-2 h-2" /> {stagesPresent}/4 stages
            </span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-stone-500">
            {first.dm_name        && <span>DM: <b className="text-stone-700">{first.dm_name}</b></span>}
            {(first.barter_pct !== undefined && first.barter_pct !== null && first.barter_pct !== '') && (
              <span>Barter: <b className="text-stone-800">{first.barter_pct}%</b></span>
            )}
            {pickAmt(first)       && <span>Expected: <b className="text-emerald-700">{pickAmt(first)}</b></span>}
            {first.area           && <span>Area: {first.area}</span>}
            {first.product_type   && <span>{first.product_type}{first.unit ? ` · ${first.unit}` : ''}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 pt-0.5">
          <span className="text-[9px] font-mono text-stone-300">#{groupId.slice(0, 8)}</span>
          {expanded
            ? <ChevronUp  className="w-4 h-4 text-stone-400" />
            : <ChevronDown className="w-4 h-4 text-stone-400" />}
        </div>
      </button>

      {/* ── Timeline ── */}
      <div className="px-4 pt-5 pb-4">
        <div className="relative">
          {/* Base line (full width, grey) */}
          <div className="absolute top-4 left-[12.5%] right-[12.5%] h-0.5 bg-stone-200 z-0" />
          {/* Progress line (colored, covers present stages) */}
          <div
            className="absolute top-4 h-0.5 z-0 transition-all duration-500"
            style={{ left: lineLeft, width: lineWidth, backgroundColor: '#10b981' }}
          />

          {/* Stage nodes */}
          <div className="relative z-10 grid grid-cols-4">
            {STAGE_SEQUENCE.map((stage) => {
              const deal      = deals[stage.key];
              const sInfo     = deal ? STATUS_LEGEND.find(s => String(s.val) === String(deal.status)) : null;
              const Icon      = stage.Icon;
              const nodeDate  = deal ? fmtDate(deal.updatedAt || deal.createdAt) : null;
              const present   = !!deal;

              return (
                <div key={stage.key} className="flex flex-col items-center gap-1.5" data-testid={`timeline-node-${groupId.slice(0,8)}-${stage.key}`}>
                  {/* Circle dot */}
                  <div
                    className="w-8 h-8 rounded-full border-2 flex items-center justify-center shadow-sm transition-all"
                    style={present ? {
                      backgroundColor: sInfo?.dot || stage.color,
                      borderColor:     sInfo?.dot || stage.color,
                    } : {
                      backgroundColor: '#f5f5f4',
                      borderColor:     '#d6d3d1',
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: present ? '#fff' : '#a8a29e' }} />
                  </div>

                  {/* Stage label */}
                  <span
                    className="text-[9px] font-bold uppercase tracking-wide"
                    style={{ color: present ? stage.color : '#a8a29e' }}
                  >
                    {stage.label}
                  </span>

                  {/* Status badge */}
                  {sInfo ? (
                    <span
                      style={{ backgroundColor: sInfo.bg, color: sInfo.fg }}
                      className="text-[8px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                    >
                      {sInfo.label}
                    </span>
                  ) : (
                    <span className="text-[8px] text-stone-300 italic">Pending</span>
                  )}

                  {/* Date */}
                  <span className="text-[8px] text-stone-400 text-center leading-tight">
                    {nodeDate || '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Expanded per-stage detail ── */}
        {expanded && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 pt-3 border-t border-stone-100">
            {STAGE_SEQUENCE.map(stage => {
              const deal = deals[stage.key];
              const Icon = stage.Icon;

              if (!deal) {
                return (
                  <div key={stage.key} className="p-2.5 rounded-lg bg-stone-50 border border-dashed border-stone-200">
                    <div className="flex items-center gap-1 mb-1">
                      <Icon className="w-3 h-3 text-stone-300" />
                      <span className="text-[9px] font-bold text-stone-400">{stage.label}</span>
                    </div>
                    <p className="text-[9px] text-stone-300 italic">Not yet promoted</p>
                  </div>
                );
              }

              const sInfo = STATUS_LEGEND.find(s => String(s.val) === String(deal.status));
              const amt   = pickAmt(deal);

              return (
                <div
                  key={stage.key}
                  className="p-2.5 rounded-lg border"
                  style={{ borderColor: `${stage.color}50`, backgroundColor: `${stage.color}08` }}
                >
                  <div className="flex items-center gap-1 mb-2">
                    <Icon className="w-3 h-3" style={{ color: stage.color }} />
                    <span className="text-[9px] font-bold" style={{ color: stage.color }}>{stage.label}</span>
                    {sInfo && (
                      <span
                        style={{ backgroundColor: sInfo.bg, color: sInfo.fg }}
                        className="ml-auto text-[8px] font-bold px-1 py-0.5 rounded"
                      >
                        {sInfo.label}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5 text-[9px] text-stone-600">
                    {deal.dm_name && (
                      <div><span className="text-stone-400">DM:</span> {deal.dm_name}</div>
                    )}
                    {(deal.barter_pct !== undefined && deal.barter_pct !== null && deal.barter_pct !== '') && (
                      <div><span className="text-stone-400">Barter%:</span> {deal.barter_pct}</div>
                    )}
                    {amt && (
                      <div><span className="text-stone-400">Amount:</span> <span className="text-emerald-700 font-semibold">{amt}</span></div>
                    )}
                    {deal.area && (
                      <div><span className="text-stone-400">Area:</span> {deal.area}</div>
                    )}
                    {deal.remark && (
                      <div className="truncate"><span className="text-stone-400">Remark:</span> {deal.remark}</div>
                    )}
                    <div className="text-stone-400 pt-0.5">{fmtDate(deal.updatedAt) || '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Full timeline view (list of all groups) ─────────────────────────────────
const TimelineView = ({ groups, loading }) => {
  const [search, setSearch] = useState('');
  const entries = Object.entries(groups || {});

  const filtered = search.trim()
    ? entries.filter(([, deals]) => {
        const first = Object.values(deals)[0] || {};
        return ['customer_name', 'dm_name', 'area', 'product_type', 'reference_person']
          .some(k => first[k] && String(first[k]).toLowerCase().includes(search.toLowerCase()));
      })
    : entries;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-stone-400 text-sm">
        Loading timeline…
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-14">
        <Link2 className="w-8 h-8 text-stone-200 mx-auto mb-3" />
        <p className="text-stone-400 text-sm font-medium">No linked deal groups yet</p>
        <p className="text-stone-300 text-xs mt-1">
          Use the <b className="text-amber-500">Promote All</b> button on any deal to create the first linked group.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="timeline-view">
      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-stone-400" />
          <input
            type="text"
            placeholder="Search by customer, DM, area…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-8 pl-8 pr-3 text-xs border border-stone-200 rounded-lg outline-none focus:ring-1 focus:ring-emerald-400 bg-white"
            data-testid="timeline-search"
          />
        </div>
        {search && (
          <button
            onClick={() => setSearch('')}
            className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600 px-2 py-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <span className="text-xs text-stone-500 shrink-0 font-medium">
          {filtered.length} / {entries.length} group{entries.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Group cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-stone-300 text-xs italic">No groups match your search.</div>
      ) : (
        filtered.map(([groupId, deals]) => (
          <DealGroupCard key={groupId} groupId={groupId} deals={deals} />
        ))
      )}
    </div>
  );
};

export default TimelineView;
